import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig';
import { buildPdfShareLink } from '../utils/api';
import '../styles/InvoicePreview.css';
import PaymentTypeModal from '../components/modals/PaymentTypeModal';
import InvoiceTypeModal from '../components/modals/InvoiceTypeModal';
import InvoiceCopyModal from '../components/modals/InvoiceCopyModal';
import EWayBillPanel from '../components/modals/EWayBillPanel';
import EInvoicePanel from '../components/modals/EInvoicePanel';
import EInvoicePrintTemplate from '../components/modals/EInvoicePrintTemplate';
import { usePermissionContext } from '../contexts/PermissionContext';
import InvoiceRenderer from '../components/invoice/InvoiceRenderer';

const InvoicePreview = ({ data: initialData, onBack }) => {
    const { hasPermission } = usePermissionContext();
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const eInvoiceRef = React.useRef(null);

    const fetchFullData = useCallback(async () => {
        const invoiceId = initialData?._id || initialData?.id;
        if (invoiceId) {
            setLoading(true);
            try {
                const token = sessionStorage.getItem('token');
                const isChallan = !!(initialData.challan_no || initialData.challanNo || initialData.isChallan);
                const endpoint = isChallan ? 'sales-challan' : 'sales-invoice';

                const res = await axios.get(`${API_BASE_URL}/${endpoint}/${invoiceId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setData({ ...res.data, isChallan });
                }
            } catch (err) {
                console.error("Error fetching full document data:", err);
            } finally {
                setLoading(false);
            }
        }
    }, [initialData]);

    useEffect(() => {
        fetchFullData();
    }, [fetchFullData]);

    // State for payment types from DB
    const [paymentTypes, setPaymentTypes] = useState([]);

    // State for Payment Type Modal
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedPaymentType, setSelectedPaymentType] = useState(data?.invoicePaymentTypeName || 'Debit');

    useEffect(() => {
        fetchPaymentTypes();
    }, []);

    const fetchPaymentTypes = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/invoice-payment-type`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPaymentTypes(res.data);
            if (!data?.invoicePaymentTypeName && res.data.length > 0) {
                setSelectedPaymentType(res.data[0].invoicePaymentTypeName);
            }
        } catch (err) {
            console.error("Error fetching payment types:", err);
        }
    };

    // State for Invoice Type Modal
    const [isInvoiceTypeModalOpen, setIsInvoiceTypeModalOpen] = useState(false);

    const getInitialInvoiceType = () => {
        if (data?.isChallan) return 'CHALLAN';
        const typeName = (data?.invoice_type_name || data?.invoiceTypeName || '').trim().toUpperCase();
        const shortName = (data?.invoice_short_name || data?.invoiceShortName || '').trim().toUpperCase();
        const no = (data?.invoice_no || data?.invoiceNo || '').trim().toUpperCase();
        if (typeName === 'AW' || shortName === 'AW' || no.startsWith('AW-')) return 'TAX INVOICE';
        if (typeName === 'PI' || shortName === 'PI' || no.startsWith('PI-')) return 'PROFORMA INVOICE';
        if (typeName === 'CH' || shortName === 'CH' || no.startsWith('CH-')) return 'CHALLAN';
        return data?.invoice_type_name || data?.invoiceTypeName || 'TAX INVOICE';
    };

    const [selectedInvoiceType, setSelectedInvoiceType] = useState(getInitialInvoiceType());

    useEffect(() => {
        setSelectedInvoiceType(getInitialInvoiceType());
    }, [data]);

    // State for Invoice Copy Modal
    const [isInvoiceCopyModalOpen, setIsInvoiceCopyModalOpen] = useState(false);
    const [selectedCopyType, setSelectedCopyType] = useState('Original');

    // State for WhatsApp
    const [mobileNo, setMobileNo] = useState('');
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

    // Helper to convert number to words (Indian System)
    const numberToWords = (num) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const inWords = (n) => {
            if (n < 20) return a[n];
            let s = b[Math.floor(n / 10)];
            if (n % 10 > 0) s += ' ' + a[n % 10].trim();
            return s;
        };

        const convert = (n) => {
            if (n === 0) return '';
            let res = '';
            if (n >= 10000000) {
                res += convert(Math.floor(n / 10000000)) + 'Crore ';
                n %= 10000000;
            }
            if (n >= 100000) {
                res += inWords(Math.floor(n / 100000)) + ' Lakh ';
                n %= 100000;
            }
            if (n >= 1000) {
                res += inWords(Math.floor(n / 1000)) + ' Thousand ';
                n %= 1000;
            }
            if (n >= 100) {
                res += inWords(Math.floor(n / 100)) + ' Hundred ';
                n %= 100;
            }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += inWords(Math.floor(n));
            }
            return res;
        };

        if (num === 0) return 'Zero Only';
        const parts = num.toFixed(2).split('.');
        let result = convert(parseInt(parts[0]));
        if (parts.length > 1 && parseInt(parts[1]) > 0) {
            result += ' and ' + inWords(parseInt(parts[1])) + ' Paise';
        }
        return result.trim() + ' Only';
    };

    // Helper to get items as an array
    const getItems = (src) => {
        const raw = src?.items;
        let arr = [];
        if (Array.isArray(raw)) arr = raw;
        else if (typeof raw === 'string') {
            try { arr = JSON.parse(raw); } catch (e) { arr = []; }
        }
        return arr.filter(item => item && (item.product || item.productName || item.product_name));
    };

    // Helper to construct image URL
    const getImageUrl = (path) => {
        if (!path || path === 'undefined' || path === 'null') return '';
        if (typeof path !== 'string') return '';

        if (path.startsWith('data:') || path.startsWith('http')) return path;

        // Handle Base64 strings without prefix
        if (path.length > 500 && !path.includes('/') && !path.includes('\\')) {
            return `data:image/png;base64,${path}`;
        }

        const cleanPath = path.replace(/\\/g, '/').replace(/^public\//, '');
        const relativePath = cleanPath.startsWith('uploads/') ? cleanPath.substring(8) : cleanPath;
        return `${API_BASE_URL}/uploads/${relativePath}`;
    };

    const itemsForCalc = getItems(data);
    const subTotal = itemsForCalc.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);

    const isGujarat = (data?.customer_state || data?.customerState || data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.Customer?.customer_state || '').toLowerCase().includes('gujarat');
    const stateCodeValue = data?.customer_state_code || data?.customerStateCode || data?.Customer?.customer_state_code || (isGujarat ? '24' : '');

    // We should strictly determine local vs interstate based on the tax rates applied
    let tax1Rate = parseFloat(data?.sale_type_tax1 || 0);
    let tax2Rate = parseFloat(data?.sale_type_tax2 || 0);

    // Fallback if missing
    if (!data?.sale_type_tax1 && !data?.sale_type_tax2) {
        const companyStateCode = data?.Company?.company_state_code || '24';
        const isSameState = String(stateCodeValue).trim() === String(companyStateCode).trim();
        tax1Rate = isSameState ? 9 : 18;
        tax2Rate = isSameState ? 9 : 0;
    }

    const isLocal = tax2Rate > 0;

    const discountVal = parseFloat(data?.discount || data?.discountPercent || 0);
    const discountAmt = (data?.discount_type === '%' || data?.discountType === '%') ? (subTotal * discountVal) / 100 : discountVal;

    const insuranceRate = parseFloat(data?.insurance || 0);
    const insuranceAmt = (subTotal * insuranceRate) / 100;

    // Support all variations of extra charges field names
    const extraAmt = parseFloat(data?.extrChargesAmount || data?.extraChargesAmount || data?.extr_charges_amount || data?.extra_charges_amount || 0);

    const taxableAmount = subTotal - discountAmt + insuranceAmt + extraAmt;

    const tax1Amt = (taxableAmount * tax1Rate) / 100;
    const tax2Amt = (taxableAmount * tax2Rate) / 100;
    const totalTaxAmt = tax1Amt + tax2Amt;

    const gTotal = taxableAmount + totalTaxAmt;


    const invoiceData = useMemo(() => {
        const company = data?.Company || data?.companyId || data?.company || {};
        return {
            invoiceNo: data?.invoiceNo || data?.invoice_no || data?.challan_no || data?.challanNo || '---',
            invoiceDate: (data?.invoiceDate || data?.invoice_date || data?.challan_date || data?.challanDate) ? new Date(data.invoiceDate || data.invoice_date || data.challan_date || data.challanDate).toLocaleDateString('en-GB') : '',
            eWayBillNo: (data?.ewb_status === 'CANCELLED' || data?.ewbStatus === 'CANCELLED') ? '' : (data?.eWayBillNo || data?.e_way_bill_no || ''),
            transporter: data?.transporterTradeName || data?.transporter_trade_name || data?.transporterName || data?.transporter_name || 'SELF',
            vehicleNo: data?.vehicleNo || data?.vehicle_no || '',
            lrNo: data?.lrNo || data?.lr_no || '',
            lrDate: (data?.lrDate || data?.lr_date) ? new Date(data.lrDate || data.lr_date).toLocaleDateString('en-GB') : '',
            containerNo: data?.containerNo || data?.container_no || '',
            sealNo: data?.sealNo || data?.seal_no || '',
            paymentTerms: data?.paymentTerms || data?.payment_terms || '',
            extraChargesName: data?.extraChargesName || data?.extra_charges_name || 'Extra Charges',
            buyer: {
                name: data?.customerTradeName || data?.customer_trade_name || data?.customerName || data?.customer_name || 'N/A',
                address: data?.customerAddress || data?.customer_address || 'N/A',
                cityState: (() => {
                    const city = data?.customerCity || data?.customer_city || '';
                    const pin = data?.customerPinCode || data?.customer_pin_code || '';
                    return city ? `${city}${pin ? ' - ' + pin : ''}` : pin;
                })() || 'N/A',
                placeOfSupply: data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.customerState || data?.customer_state || 'N/A',
                stateCode: stateCodeValue || 'N/A',
                gstin: data?.customerGst || data?.customer_gst || 'N/A',
                pan: data?.customerPanNo || data?.customer_pan_no || 'N/A',
                realName: data?.customerName || data?.customer_name || ''
            },
            consignee: {
                name: data?.customerTradeName || data?.customer_trade_name || data?.customerName || data?.customer_name || 'N/A',
                address: data?.customerDeliveryAddress || data?.customer_delivery_address || data?.customerAddress || data?.customer_address || 'N/A',
                cityState: (() => {
                    const city = data?.customerCity || data?.customer_city || '';
                    const pin = data?.customerPinCode || data?.customer_pin_code || '';
                    return city ? `${city}${pin ? ' - ' + pin : ''}` : pin;
                })() || 'N/A',
                placeOfSupply: data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.customerState || data?.customer_state || 'N/A',
                stateCode: stateCodeValue || 'N/A',
                gstin: data?.customerGst || data?.customer_gst || 'N/A',
                pan: data?.customerPanNo || data?.customer_pan_no || 'N/A'
            },
            items: itemsForCalc.map((it, idx) => ({
                srNo: idx + 1,
                category: it.category || '',
                product: it.product || '',
                model: it.modelNumber || it.sizeName || it.size_name || it.size || '',
                hsn: it.hsnCode || '',
                grade: it.grade || '',
                color: it.color || '',
                qty: parseFloat(it.quantity || 0).toFixed(2),
                unit: it.unit || it.qtyUnit || 'NOS',
                rate: parseFloat(it.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                amount: parseFloat(it.total || it.amount || (parseFloat(it.quantity) * parseFloat(it.rate)) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })
            })),
            totals: {
                qty: itemsForCalc.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0).toFixed(2),
                subTotal: subTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                discount: discountAmt.toFixed(2),
                discountPercent: discountVal,
                discountType: data?.discount_type || data?.discountType || '%',
                taxableAmount: taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                amount: taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }), // Keep amount alias for backward compatibility
                words: numberToWords(gTotal),
                insurance: insuranceAmt.toFixed(2),
                insurancePercent: insuranceRate,
                extra: extraAmt.toFixed(2),
                tax1: tax1Amt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                tax2: tax2Amt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                totalTax: totalTaxAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                tax1Rate: tax1Rate,
                tax2Rate: tax2Rate,
                isLocal: isLocal,
                grandTotal: gTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                gstPercent: (tax1Rate + tax2Rate) + '%'
            },
            bank: {
                name: data?.invoiceCompanyBankName || data?.invoice_company_bank_name || company?.company_bank_name || company?.companyBankName || 'N/A',
                accountName: data?.invoiceCompanyBankAccountName || data?.invoice_company_bank_account_name || company?.company_bank_account_name || company?.companyBankAccountName || 'N/A',
                accountNumber: data?.invoiceCompanyBankAccountNumber || data?.invoice_company_bank_account_number || company?.company_bank_account_number || company?.companyBankAccountNumber || 'N/A',
                ifsc: data?.invoiceCompanyBankIfscCode || data?.invoice_company_bank_ifsc_code || company?.company_bank_ifsc_code || company?.companyBankIfscCode || 'N/A'
            }
        };
    }, [data, subTotal, insuranceAmt, extraAmt, totalTaxAmt, gTotal, itemsForCalc, stateCodeValue, tax1Rate, tax2Rate, isLocal, discountVal, discountAmt, taxableAmount]);

    const handlePaymentSubmit = (type) => {
        setSelectedPaymentType(type);
        console.log("Selected Payment Type:", type);
    };

    const handleInvoiceTypeSubmit = (type) => {
        setSelectedInvoiceType(type);
        console.log("Selected Invoice Type:", type);
    };

    const handleInvoiceCopySubmit = (type) => {
        setSelectedCopyType(type);
        console.log("Selected Copy Type:", type);
    };

    // Handle E-Invoice PDF Download
    const handleDownloadEIPDF = async () => {
        if (!eInvoiceRef.current) return;

        toast.info('Generating E-Invoice PDF...');
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        try {
            // Use html2canvas to capture the E-Invoice template
            const canvas = await html2canvas(eInvoiceRef.current, {
                scale: 2, // Higher scale for better quality
                padding: 0,
                backgroundColor: '#ffffff',
                useCORS: true
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
            const margin = 0; // The template already has 10mm padding, so no extra jsPDF margin needed
            const renderWidth = pdfWidth - (margin * 2);

            const imgProps = pdf.getImageProperties(imgData);
            const renderHeight = (imgProps.height * renderWidth) / imgProps.width;

            // Add image to PDF
            pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);

            // Save PDF
            pdf.save(`EInvoice_${invoiceData.invoiceNo}.pdf`);
            toast.success('E-Invoice PDF downloaded successfully!');
        } catch (err) {
            console.error('Error generating E-Invoice PDF:', err);
            toast.error('Failed to generate E-Invoice PDF');
        }
    };

    // Handle PDF Download
    const handleDownloadPDF = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        const elements = document.querySelectorAll('.invoice-box');
        const pdf = new jsPDF('p', 'mm', 'a4');

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];

            // Add PDF capture mode to force A4 dimensions for accurate rendering
            el.classList.add('pdf-capture-mode');

            try {
                // Wait a tiny bit for the browser to apply the PDF mode CSS rules
                await new Promise(resolve => setTimeout(resolve, 100));

                // Use html2canvas to capture the invoice-box
                const canvas = await html2canvas(el, {
                    scale: 2, // Higher scale for better quality
                    padding: 0,
                    backgroundColor: '#ffffff',
                    useCORS: true // Important for images
                });

                const imgData = canvas.toDataURL('image/png');
                if (i > 0) {
                    pdf.addPage();
                }

                const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
                const margin = 4; // 4mm margin on all sides
                const renderWidth = pdfWidth - (margin * 2);

                const imgProps = pdf.getImageProperties(imgData);
                const renderHeight = (imgProps.height * renderWidth) / imgProps.width;

                // Add image to PDF with margin
                pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);
            } finally {
                // Remove PDF capture mode
                el.classList.remove('pdf-capture-mode');
            }
        }

        // Save PDF
        pdf.save(`Invoice_${invoiceData.invoiceNo}.pdf`);
    };

    // Handle PDF Download Without Letter
    const handleDownloadPDFWithoutLetter = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        const elements = document.querySelectorAll('.invoice-box');
        const pdf = new jsPDF('p', 'mm', 'a4');

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            const headerImageContainers = el.querySelectorAll('.header-image-container');
            const signatureContainers = el.querySelectorAll('.auth-sign-img');

            // Store original display styles
            const originalDisplayHeaders = Array.from(headerImageContainers).map(c => c.style.display || '');
            const originalDisplaySignatures = Array.from(signatureContainers).map(c => c.style.display || '');

            // Hide header and signature for capture
            headerImageContainers.forEach(c => c.style.display = 'none');
            signatureContainers.forEach(c => c.style.display = 'none');

            // Force A4 dimensions
            el.classList.add('pdf-capture-mode');

            try {
                // Wait a tiny bit for the browser to apply the PDF mode CSS rules
                await new Promise(resolve => setTimeout(resolve, 100));

                // Use html2canvas to capture the invoice-box without header
                const canvas = await html2canvas(el, {
                    scale: 2, // Higher scale for better quality
                    padding: 0,
                    backgroundColor: '#ffffff',
                    useCORS: true
                });

                const imgData = canvas.toDataURL('image/png');
                if (i > 0) {
                    pdf.addPage();
                }

                const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
                const margin = 4; // 4mm margin
                const renderWidth = pdfWidth - (margin * 2);

                const imgProps = pdf.getImageProperties(imgData);
                const renderHeight = (imgProps.height * renderWidth) / imgProps.width;

                // Add image to PDF
                pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);
            } finally {
                // Restore visibility and styling
                el.classList.remove('pdf-capture-mode');
                headerImageContainers.forEach((c, idx) => c.style.display = originalDisplayHeaders[idx]);
                signatureContainers.forEach((c, idx) => c.style.display = originalDisplaySignatures[idx]);
            }
        }

        // Save PDF
        pdf.save(`Invoice_NoLetter_${invoiceData.invoiceNo}.pdf`);
    };

    // Handle Print With Letter
    const handlePrintWithLetter = () => {
        window.print();
    };

    // Handle Print Without Letter
    const handlePrintWithoutLetter = () => {
        const elements = document.querySelectorAll('.invoice-box');
        const originals = [];

        elements.forEach((el, index) => {
            const headerImageContainers = el.querySelectorAll('.header-image-container');
            const signatureContainers = el.querySelectorAll('.auth-sign-img');

            const headerStyles = Array.from(headerImageContainers).map(c => c.style.display || '');
            const signatureStyles = Array.from(signatureContainers).map(c => c.style.display || '');

            originals.push({
                headerImageContainers,
                signatureContainers,
                headerStyles,
                signatureStyles
            });

            headerImageContainers.forEach(c => c.style.display = 'none');
            signatureContainers.forEach(c => c.style.display = 'none');
        });

        window.print();

        elements.forEach((el, index) => {
            const orig = originals[index];
            orig.headerImageContainers.forEach((c, idx) => c.style.display = orig.headerStyles[idx]);
            orig.signatureContainers.forEach((c, idx) => c.style.display = orig.signatureStyles[idx]);
        });
    };

    // Handle Send WhatsApp - validates number, generates PDF on backend, and triggers WhatsApp Web
    const handleSendWhatsApp = async () => {
        if (!mobileNo || !mobileNo.trim()) {
            toast.error('Please enter a mobile number');
            return;
        }

        // Format validation: accept +919876543210, 919876543210, 9876543210
        let cleaned = mobileNo.trim().replace(/[^\d+]/g, '');
        if (cleaned.startsWith('+91')) {
            cleaned = cleaned.substring(1);
        } else if (cleaned.length === 10) {
            cleaned = '91' + cleaned;
        }

        const phoneRegex = /^91\d{10}$/;
        if (!phoneRegex.test(cleaned)) {
            toast.error('Invalid mobile number format. Please enter a valid 10-digit number.');
            return;
        }

        const invoiceId = data?._id || data?.id;
        if (!invoiceId) {
            toast.error('Invoice ID is missing. Cannot send invoice.');
            return;
        }

        setIsSendingWhatsApp(true);

        try {
            const token = sessionStorage.getItem('token');
            const endpoint = data?.isChallan ? 'sales-challan' : 'invoice';
            const response = await axios.post(
                `${API_BASE_URL}/${endpoint}/generate-whatsapp-link`,
                {
                    invoiceId,
                    invoiceType: selectedInvoiceType,
                    paymentType: selectedPaymentType,
                    copyType: selectedCopyType
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data && response.data.success) {
                const { invoiceNumber, invoiceDate, customerName, totalAmount, pdfUrl } = response.data;
                const companyName = data?.invoice_company_name || data?.Company?.company_name || 'ASENSIA INDUSTRY';

                // Format professional WhatsApp message matching specifications
                const message = `Hello ${customerName},

Thank you for your business.

${data?.isChallan ? 'Challan Details:' : 'Invoice Details:'}
${data?.isChallan ? 'Challan Number:' : 'Invoice Number:'} ${invoiceNumber}
${data?.isChallan ? 'Challan Date:' : 'Invoice Date:'} ${invoiceDate}
Amount: â‚¹${parseFloat(totalAmount).toLocaleString('en-IN')}

Download ${data?.isChallan ? 'Challan PDF:' : 'Invoice PDF:'}
${pdfUrl}

Regards,
${companyName}`;

                // Open WhatsApp Web with dynamic payload
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                const whatsappUrl = isMobile
                    ? `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`
                    : `https://web.whatsapp.com/send?phone=${cleaned}&text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');

                toast.success('WhatsApp URL created successfully!');
            } else {
                toast.error(response.data?.message || 'Failed to generate invoice link.');
            }
        } catch (error) {
            console.error('Error generating WhatsApp shareable link:', error);
            const errorMsg = error.response?.data?.message || 'Server error occurred during PDF generation.';
            toast.error(errorMsg);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };

    if (loading) return <div className="inv-preview-container">Loading...</div>;

    return (
        <div className="inv-preview-container">
            <div className="inv-preview-main">
                <div className="inv-top-controls">
                    <button className="inv-control-btn" onClick={() => setIsPaymentModalOpen(true)}>Payment Type</button>
                    <button className="inv-control-btn" onClick={() => setIsInvoiceTypeModalOpen(true)}>Invoice Type</button>
                    <button className="inv-control-btn" onClick={() => setIsInvoiceCopyModalOpen(true)}>Invoice Copy</button>
                    {onBack && <button className="inv-control-btn" onClick={onBack} style={{ marginLeft: 'auto', background: '#d33' }}>Close Preview</button>}
                </div>

                <InvoiceRenderer
                    data={data}
                    invoiceData={invoiceData}
                    selectedPaymentType={selectedPaymentType}
                    selectedInvoiceType={selectedInvoiceType}
                    selectedCopyType={selectedCopyType}
                    getImageUrl={getImageUrl}
                />

            </div>

            <div className="inv-sidebar">
                {!data?.isChallan && (
                    <>
                        {hasPermission('E-Way Bill', 'add') && (
                            <div style={{ marginBottom: '15px' }}>
                                <EWayBillPanel invoiceData={data} onRefresh={fetchFullData} />
                            </div>
                        )}

                        {hasPermission('E-Invoice', 'add') && (
                            <div style={{ marginBottom: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                                <EInvoicePanel invoiceData={data} onRefresh={fetchFullData} onDownloadPDF={handleDownloadEIPDF} />
                            </div>
                        )}
                    </>
                )}

                <button className="sidebar-btn" onClick={handleDownloadPDF}>With Letter PDF</button>
                <button className="sidebar-btn" onClick={handleDownloadPDFWithoutLetter}>Without Letter PDF</button>
                <button className="sidebar-btn" onClick={handlePrintWithLetter}>With Letter Print</button>
                <button className="sidebar-btn" onClick={handlePrintWithoutLetter}>Without Letter Print</button>


                <input
                    type="text"
                    placeholder="Enter Mobile Number"
                    className="sidebar-input"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                />
                <button
                    className="sidebar-btn sidebar-btn-whatsapp"
                    onClick={handleSendWhatsApp}
                    disabled={isSendingWhatsApp}
                >
                    {isSendingWhatsApp ? 'Sending...' : 'Send WhatsApp'}
                </button>
            </div>

            {/* Payment Type Modal */}
            <PaymentTypeModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                onSubmit={handlePaymentSubmit}
                currentType={selectedPaymentType}
            />

            {/* Invoice Type Modal */}
            <InvoiceTypeModal
                isOpen={isInvoiceTypeModalOpen}
                onClose={() => setIsInvoiceTypeModalOpen(false)}
                onSubmit={handleInvoiceTypeSubmit}
                currentType={selectedInvoiceType}
            />

            {/* Invoice Copy Modal */}
            <InvoiceCopyModal
                isOpen={isInvoiceCopyModalOpen}
                onClose={() => setIsInvoiceCopyModalOpen(false)}
                onSubmit={handleInvoiceCopySubmit}
            />

            {/* Hidden E-Invoice Print Template */}
            <div style={{ overflow: 'hidden', height: 0 }}>
                <EInvoicePrintTemplate ref={eInvoiceRef} data={data} invoiceData={invoiceData} />
            </div>
        </div>
    );
};

export default InvoicePreview;
